// Role-aware Help / how-it-works. One guide per user flow (sender, recipient,
// merchant, admin), reachable from each portal's "Help" nav item. Kept in sync
// with the real flow — verification is recipient-side, payouts after confirm,
// disputes hold payouts, etc.

import { useAuth } from '../contexts/AuthContext';
import { PortalShell, type NavSection } from '../components/shared/PortalShell';
import { Card } from '../components/shared/primitives';
import { CUSTOMER_NAV, RECIPIENT_NAV, MERCHANT_NAV, ADMIN_NAV } from './_navs';
import {
  ShoppingBag, Truck, Store, ShieldCheck, ListChecks, MessageCircleQuestion,
} from 'lucide-react';

interface Step { t: string; d: string }
interface Section { title: string; steps: Step[] }
interface Guide { icon: React.ComponentType<{ className?: string }>; title: string; intro: string; sections: Section[]; faq: { q: string; a: string }[] }

const SENDER: Guide = {
  icon: ShoppingBag,
  title: 'Sending support to your family',
  intro: 'VoyeLakay lets you buy what your family in Haiti needs — fulfilled by an approved local merchant — and proves it was received.',
  sections: [
    { title: '1 · Add your recipient', steps: [
      { t: 'Add a family member', d: 'Recipients → Add. Enter their name, WhatsApp number, city, and optionally an email. We auto-send them an invite (WhatsApp + email) with a one-tap link to their own VoyeLakay.' },
      { t: 'They don’t need an account first', d: 'The invite creates a passwordless account for them on the spot — they just tap the link.' },
    ]},
    { title: '2 · Send a request', steps: [
      { t: 'Three ways', d: 'Build a cart from a specific merchant, send a ready-made Care package, or use the Basket planner to model a household’s needs (people + tier) and start from that.' },
      { t: 'Pick the recipient + items', d: 'Choose who it’s for and add items. Submit — nearby merchants (or the one you chose) get the request.' },
    ]},
    { title: '3 · Review the quote & pay', steps: [
      { t: 'Approve the price', d: 'A merchant prices the items and sends a quote in USD (goods + delivery + platform fee, at the live FX rate). Approve or decline.' },
      { t: 'Pay', d: 'Card checkout (Stripe) is coming; for now a demo bridge sends the paid order to the merchant.' },
    ]},
    { title: '4 · Track & verify', steps: [
      { t: 'Watch fulfillment', d: 'Preparing → ready → out for delivery → delivered. The merchant can attach a delivery photo.' },
      { t: 'Your family confirms — not you', d: 'The delivery code goes to your recipient. They confirm receipt (in their app, by WhatsApp, or by giving the code to the merchant at the door). You relay the code if needed, but you can’t self-confirm — that’s what makes “verified” mean something.' },
      { t: 'Get the receipt', d: 'The moment they confirm, an official delivery receipt is emailed to everyone and downloadable from the order.' },
    ]},
    { title: '5 · If something’s wrong', steps: [
      { t: 'Report a problem', d: 'On a delivered/confirmed order, use “Report a problem.” That opens a dispute and holds the merchant’s payout while VoyeLakay reviews — verified, or refunded.' },
    ]},
  ],
  faq: [
    { q: 'Why can’t I confirm receipt myself?', a: 'Because the promise is that your family actually received it. Only the recipient (or the merchant entering the recipient’s code at the door) can confirm.' },
    { q: 'What’s the platform fee?', a: 'A small percentage of goods + delivery, shown in the quote before you approve. The merchant is paid in HTG; VoyeLakay’s margin is the USD markup.' },
    { q: 'Where does the money go?', a: 'The merchant is paid after the recipient confirms receipt.' },
  ],
};

const RECIPIENT: Guide = {
  icon: Truck,
  title: 'Receiving & confirming deliveries',
  intro: 'Your family in the diaspora can buy what you need from a merchant near you. You confirm when it arrives.',
  sections: [
    { title: '1 · Getting in', steps: [
      { t: 'Open your invite link', d: 'You’ll get a WhatsApp or email from your family with a link. Tap it — you’re signed in, no password needed.' },
    ]},
    { title: '2 · Ask for what you need', steps: [
      { t: 'New request', d: 'Tap New request, pick items (or use the Basket planner), and send it. Your family sees it and funds it.' },
    ]},
    { title: '3 · Confirm your delivery', steps: [
      { t: 'You get a code', d: 'When a merchant marks your order delivered, you receive a delivery code (by WhatsApp, or from your family).' },
      { t: 'Confirm receipt', d: 'On your Home screen, the delivered order shows a “Confirm your delivery” box — enter the code. You can also reply to the WhatsApp with the code, or read it to the merchant at the door.' },
      { t: 'Done', d: 'That confirms receipt, releases the merchant’s payment, and sends everyone the receipt.' },
    ]},
    { title: '4 · Your history', steps: [
      { t: 'Past deliveries', d: 'History shows every confirmed order with its receipt to download.' },
    ]},
  ],
  faq: [
    { q: 'I lost the code — what now?', a: 'Ask your family to resend it, or check the WhatsApp message from the delivery. Your family can re-share the code from their order.' },
    { q: 'Do I need to pay?', a: 'No. Your family in the diaspora pays. You receive and confirm.' },
  ],
};

const MERCHANT: Guide = {
  icon: Store,
  title: 'Fulfilling orders & getting paid',
  intro: 'Approved merchants stock a store, quote incoming requests, deliver, and get paid in HTG after the recipient confirms.',
  sections: [
    { title: '1 · Get approved (KYB)', steps: [
      { t: 'Register or claim your store', d: 'Sign up as a merchant, or claim a pre-listed store. Complete your business profile (representative, address, payout method).' },
      { t: 'Verification', d: 'VoyeLakay reviews and approves you (KYB). Once approved you get a Merchant ID and a verification certificate.' },
    ]},
    { title: '2 · Stock your store', steps: [
      { t: 'Add from catalog', d: 'Inventory → “Add from catalog” flips common items (groceries, household, pharmacy) into your store with a suggested price — toggle them on, then edit prices.' },
      { t: 'Add custom items', d: 'Add anything else with “Add item.” Set name, unit, price, and availability.' },
    ]},
    { title: '3 · Quote requests', steps: [
      { t: 'Inbox', d: 'Quote requests shows carts addressed to you or open in your city. Open one to price the items.' },
      { t: 'Send a quote', d: 'Adjust prices to today’s stock, set delivery fee + ETA, and send. The customer reviews and approves.' },
    ]},
    { title: '4 · Fulfill & confirm', steps: [
      { t: 'Advance the order', d: 'Active orders: preparing → ready → out for delivery → delivered. Upload a delivery photo as proof.' },
      { t: 'Confirm at the door', d: 'The recipient confirms in their app/WhatsApp — or reads you their delivery code, which you enter under “Confirm at door.”' },
    ]},
    { title: '5 · Get paid', steps: [
      { t: 'Payouts', d: 'When an order is confirmed, a payout (HTG) is created. Payouts shows your pending and paid amounts; VoyeLakay disburses via MonCash.' },
    ]},
  ],
  faq: [
    { q: 'When do I get paid?', a: 'After the recipient confirms receipt. Disputes pause a payout until resolved.' },
    { q: 'Why upload a delivery photo?', a: 'Proof-of-delivery is part of the trust guarantee — it’s embedded in the receipt and protects you in a dispute.' },
  ],
};

const ADMIN: Guide = {
  icon: ShieldCheck,
  title: 'Operating the platform',
  intro: 'Admin controls the catalog, rates, merchant verification, payouts, and disputes.',
  sections: [
    { title: 'Merchants (KYB)', steps: [
      { t: 'Approve / suspend', d: 'Merchants shows applicants and their KYB. Approve to issue a Merchant ID + certificate, or suspend.' },
    ]},
    { title: 'Catalog & rates', steps: [
      { t: 'Catalog', d: 'Manage service categories and cities served (with coordinates for nearest-merchant routing).' },
      { t: 'FX rates', d: 'Set the USD→HTG rate. New quotes snapshot it; existing orders keep their rate.' },
      { t: 'Fees', d: 'Set the platform take-rate, scoped by category and/or city. Most specific rule wins.' },
    ]},
    { title: 'Money & trust', steps: [
      { t: 'Payouts', d: 'Review eligible payouts and batch-disburse via MonCash.' },
      { t: 'Disputes', d: 'Open disputes hold the merchant’s payout. Resolve: issue refund / charge merchant (payout withheld) or no action (payout released).' },
    ]},
  ],
  faq: [
    { q: 'Does changing FX re-price old orders?', a: 'No. Quotes snapshot the rate at creation; only new quotes use the new rate.' },
    { q: 'What does opening a dispute do?', a: 'It immediately holds the merchant’s payout until you resolve it.' },
  ],
};

const GUIDES: Record<string, Guide> = { customer: SENDER, recipient: RECIPIENT, merchant: MERCHANT, admin: ADMIN, super_admin: ADMIN };
const NAVS: Record<string, NavSection[]> = { customer: CUSTOMER_NAV, recipient: RECIPIENT_NAV, merchant: MERCHANT_NAV, admin: ADMIN_NAV, super_admin: ADMIN_NAV };

export function HelpPage() {
  const { user } = useAuth();
  const role = user?.role ?? 'customer';
  const guide = GUIDES[role] ?? SENDER;
  const nav = NAVS[role] ?? CUSTOMER_NAV;
  const Icon = guide.icon;

  return (
    <PortalShell nav={nav} activeId="help" title="Help" subtitle="How VoyeLakay works for you.">
      <div className="space-y-5 max-w-3xl">
        <Card className="bg-gradient-to-br from-brand-50 to-[var(--color-warning-bg)]/30 border-brand-100">
          <div className="flex items-start gap-3">
            <span className="inline-flex w-11 h-11 shrink-0 items-center justify-center rounded-xl bg-white text-brand-600 shadow-sm"><Icon className="w-5 h-5" /></span>
            <div>
              <h2 className="text-[17px] font-semibold text-[var(--color-ink)]">{guide.title}</h2>
              <p className="mt-1 text-[13px] text-[var(--color-secondary)] leading-relaxed">{guide.intro}</p>
            </div>
          </div>
        </Card>

        {guide.sections.map((s) => (
          <Card key={s.title}>
            <p className="text-[14px] font-semibold text-[var(--color-ink)] inline-flex items-center gap-1.5"><ListChecks className="w-4 h-4 text-brand-500" /> {s.title}</p>
            <div className="mt-3 space-y-3">
              {s.steps.map((st) => (
                <div key={st.t} className="border-l-2 border-[var(--color-hairline)] pl-3">
                  <p className="text-[13px] font-semibold text-[var(--color-ink)]">{st.t}</p>
                  <p className="text-[12.5px] text-[var(--color-secondary)] leading-relaxed">{st.d}</p>
                </div>
              ))}
            </div>
          </Card>
        ))}

        <Card>
          <p className="text-[14px] font-semibold text-[var(--color-ink)] inline-flex items-center gap-1.5"><MessageCircleQuestion className="w-4 h-4 text-brand-500" /> Common questions</p>
          <div className="mt-3 divide-y divide-[var(--color-hairline)]">
            {guide.faq.map((f) => (
              <div key={f.q} className="py-2.5">
                <p className="text-[13px] font-semibold text-[var(--color-ink)]">{f.q}</p>
                <p className="text-[12.5px] text-[var(--color-secondary)] mt-0.5 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </Card>

        <p className="text-[11.5px] text-[var(--color-muted)] text-center">Still stuck? Email support@lakaycart.com.</p>
      </div>
    </PortalShell>
  );
}
